/*
 * Pure emit layer for the widget's analytics tracking.
 *
 * Every tracking moment fires two things at once, from the light DOM (`window`),
 * never from inside the sealed Shadow DOM, so external listeners can reach it:
 *   a) a JavaScript CustomEvent on `window` (for direct listeners / non-GTM setups)
 *   b) a `dataLayer` push (for Google Tag Manager)
 *
 * The same name string is used for both the CustomEvent type and the dataLayer
 * `event` value, so there is a single vocabulary to document.
 *
 * In production the app runs inside a cross-origin iframe, so `window` here is the
 * iframe window - useless to the customer. When framed we forward the moment to the
 * host shell, which re-emits it on the real page `window`/`dataLayer` (see host-shell),
 * keeping the public analytics contract identical.
 */
import {
    type WidgetTrackingDetailTyping,
    type WidgetTrackingEventNameTyping,
} from '@shared/typings';
import { buildMessage, FRAME_TO_HOST, isFramedContext } from '@/frame/bridge';

declare global {
    interface Window {
        dataLayer: Record<string, unknown>[]
    }
}

// Dual-emit a single tracking moment.
export const constellTrack = (
    name: WidgetTrackingEventNameTyping,
    detail: WidgetTrackingDetailTyping,
): void => {
    // Inside the iframe: hand the moment to the host shell to emit on the real page.
    if (isFramedContext()) {
        window.parent?.postMessage(buildMessage(FRAME_TO_HOST.TRACK, {
            name,
            detail,
        }), '*');
        return;
    }

    // a) CustomEvent — for direct JS listeners / non-GTM setups
    window.dispatchEvent(new CustomEvent(name, {
        bubbles: true,
        detail,
    }));

    // b) dataLayer push — for Google Tag Manager
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: name,
        ...detail,
    });
};

// Generate one id when the widget opens; reused on every event until the widget
// closes, so analytics can reconstruct a single visitor's path per visit.
export const generateWidgetSessionId = (): string =>
    self.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;

/*
 * Defensively pull a request identifier out of the (untyped) quote/create response
 * so analytics can de-duplicate submissions. Falls back to null when nothing usable
 * is present rather than emitting a misleading value.
 */
export const extractRequestId = (responseData: unknown): string | number | null => {
    if (typeof responseData !== 'object' || responseData === null) return null;

    const record = responseData as Record<string, unknown>;
    const candidate = record.id ?? record.iri ?? record.uuid ?? record.quoteId;

    return typeof candidate === 'string' || typeof candidate === 'number' ? candidate : null;
};
